import RepositoryBase from "./repositoryBase";
import type BackOfficeConnector from "../data/backOfficeConnector";
import type EventBus from "../services/eventBus";
import type { IPagination } from "../data/types";
import { excludeEmptyValues, serializeFilters } from "../utils/filters";
import AccountingDocumentCandidateResponse from "../entities/accountingDocumentCandidateResponse";
import type AccountingDocumentCandidateSearchRequest from "../entities/accountingDocumentCandidateSearchRequest";
import AccountingDocumentCandidateDetailResponse from "../entities/accountingDocumentCandidateDetailResponse";
import AccountingDocumentCandidateRowResponse from "../entities/accountingDocumentCandidateRowResponse";
import type { IApprovalRepository } from "./types";
import type AccountingDocumentCandidateApprovalsRequest from "../entities/accountingDocumentCandidateApprovalsRequest";

export default class AccountingDocumentCandidatesRepository
  extends RepositoryBase<BackOfficeConnector>
  implements IApprovalRepository
{
  constructor(protected apiFactory: ReturnType<typeof BackOfficeConnector.Factory>, protected eventBus: EventBus) {
    super(apiFactory, eventBus);
  }

  getAll(pagination: IPagination, filter: AccountingDocumentCandidateSearchRequest) {
    const query = { ...filter, _approvalState: undefined };

    return this.callApi(api =>
      api
        .all("finance")
        .all("accounting")
        .all("general_ledger")
        .all("accounting_document_candidate_search")
        .postPagedEntities<AccountingDocumentCandidateResponse>(
          {
            ...excludeEmptyValues(serializeFilters(query, false)),
            ...pagination,
          },
          AccountingDocumentCandidateResponse
        )
    );
  }

  getAllRows(itemId: string, pagination: IPagination) {
    return this.callApi(api =>
      api
        .all("finance")
        .all("accounting")
        .all("general_ledger")
        .one("accounting_document_candidates", itemId)
        .all("row_search")
        .postPagedEntities<AccountingDocumentCandidateRowResponse>(pagination, AccountingDocumentCandidateRowResponse)
    );
  }

  get(itemId: string) {
    return this.callApi(api =>
      api
        .all("finance")
        .all("accounting")
        .all("general_ledger")
        .one("accounting_document_candidates", itemId)
        .getEntity(AccountingDocumentCandidateDetailResponse)
    );
  }

  export() {
    return this.callApi(api =>
      api.all("finance").all("accounting").all("general_ledger").all("accounting_document_candidates").all("exports").postOnly({})
    );
  }

  approvals(request: AccountingDocumentCandidateApprovalsRequest) {
    return this.callApi(api =>
      api
        .all("finance")
        .all("accounting")
        .all("general_ledger")
        .all("accounting_document_candidates")
        .all("approvals")
        .postOnly(request)
    );
  }

  approve(candidateId: string) {
    return this.callApi(api =>
      api
        .all("finance")
        .all("accounting")
        .all("general_ledger")
        .one("accounting_document_candidates", candidateId)
        .all("approvals")
        .postOnly({})
    );
  }
}
